import React, { useMemo } from 'react';
import { Handshake } from '@phosphor-icons/react';

interface CoachingRow {
    employeeName: string;
    userId: string;
    engagementId?: string | number | null;
    progress?: number | null;
    completedSessions?: number | null;
    totalSessions?: number | null;
    coachName?: string | null;
}

interface TeamCoachingSnapshotWidgetProps {
    data?: CoachingRow[] | null;
    loading?: boolean;
    error?: string;
}

const getInitials = (name: string): string =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

export const TeamCoachingSnapshotWidget: React.FC<TeamCoachingSnapshotWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    const stats = useMemo(() => {
        const active = rows.filter(r => r.engagementId).length;
        const noCoach = rows.filter(r => !r.engagementId).length;
        return { active, noCoach };
    }, [rows]);

    return (
        <div className="flex flex-col h-full">
<div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Handshake className="w-8 h-8" />
                        <span className="text-sm">No coaching data</span>
                    </div>
                ) : (
                    <>
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="text-center p-2 bg-emerald-50 rounded-lg">
                                <div className="text-lg font-bold text-emerald-600">{stats.active}</div>
                                <div className="text-[10px] text-gray-400">Active Engagements</div>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded-lg">
                                <div className="text-lg font-bold text-gray-500">{stats.noCoach}</div>
                                <div className="text-[10px] text-gray-400">No Coach Assigned</div>
                            </div>
                        </div>

                        {/* Per-person rows */}
                        <div className="space-y-2.5">
                            {rows.map((row, idx) => {
                                const hasEngagement = !!row.engagementId;
                                const progress = Number(row.progress) || 0;
                                const completed = Number(row.completedSessions) || 0;
                                const total = Number(row.totalSessions) || 0;
                                return (
                                    <div
                                        key={row.userId || idx}
                                        className={`flex items-center gap-3 ${!hasEngagement ? 'opacity-50' : ''}`}
                                    >
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                            hasEngagement ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {getInitials(row.employeeName || '??')}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-0.5">
                                                <span className="text-sm text-gray-800 truncate">{row.employeeName}</span>
                                                {hasEngagement && (
                                                    <span className="text-[10px] text-gray-400 shrink-0 ml-2">
                                                        {completed}/{total} sessions
                                                    </span>
                                                )}
                                            </div>
                                            {hasEngagement ? (
                                                <>
                                                    <div className="text-[11px] text-gray-500 mb-1">
                                                        Coach: {row.coachName || 'Unassigned'}
                                                    </div>
                                                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                                            style={{ width: `${progress}%` }}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-[11px] text-gray-400">No active engagement</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
